import { useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { emit } from '@/lib/events';
import { useUnitTerms } from '@/features/map-core/useUnitTerms';

const STORAGE_KEY = 'relocation_tour_v1_completed';

type TourSelector = 'search' | 'layer' | 'poi' | 'shortlist';

interface TourStep {
  selector: TourSelector;
  titleKey: string;
  bodyKey: string;
}

const STEPS: TourStep[] = [
  { selector: 'search', titleKey: 'tour.search.title', bodyKey: 'tour.search.body' },
  { selector: 'layer', titleKey: 'tour.layer.title', bodyKey: 'tour.layer.body' },
  { selector: 'poi', titleKey: 'tour.poi.title', bodyKey: 'tour.poi.body' },
  { selector: 'shortlist', titleKey: 'tour.shortlist.title', bodyKey: 'tour.shortlist.body' },
];

const CARD_W = 288; // соответствует max-w-xs = 18rem
const GAP = 12;
const VIEWPORT_MARGIN = 8;

interface Position {
  top: number;
  left: number;
  ready: boolean;
}

interface AnchorRect {
  top: number;
  left: number;
  width: number;
  height: number;
  ready: boolean;
}

const EMPTY_ANCHOR: AnchorRect = { top: 0, left: 0, width: 0, height: 0, ready: false };

function isCompleted(): boolean {
  try {
    return !!localStorage.getItem(STORAGE_KEY);
  } catch {
    return true; // если localStorage недоступен — не показываем тур
  }
}

function markCompleted(value: 'done' | 'skipped') {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* no-op */
  }
}

function computePosition(rect: DOMRect | null): Position {
  if (!rect) return { top: 0, left: 0, ready: false };
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Сначала пробуем разместить под якорем
  let top = rect.bottom + GAP;
  let left = rect.left;

  // Если выходит за нижний край — пытаемся над якорем (примерная высота карточки 180)
  const estCardH = 180;
  if (top + estCardH > vh - VIEWPORT_MARGIN) {
    const aboveTop = rect.top - GAP - estCardH;
    if (aboveTop >= VIEWPORT_MARGIN) {
      top = aboveTop;
    } else {
      // Иначе прижимаем к верху
      top = Math.max(VIEWPORT_MARGIN, vh - estCardH - VIEWPORT_MARGIN);
    }
  }

  // Горизонтальное удержание в пределах viewport
  if (left + CARD_W > vw - VIEWPORT_MARGIN) {
    left = vw - CARD_W - VIEWPORT_MARGIN;
  }
  if (left < VIEWPORT_MARGIN) {
    left = VIEWPORT_MARGIN;
  }

  return { top, left, ready: true };
}

export function OnboardingTour() {
  const { t } = useTranslation();
  const terms = useUnitTerms();
  const [stepIndex, setStepIndex] = useState(0);
  const [visible, setVisible] = useState<boolean>(() => !isCompleted());
  const [pos, setPos] = useState<Position>({ top: 0, left: 0, ready: false });
  const [anchor, setAnchor] = useState<AnchorRect>(EMPTY_ANCHOR);
  // tick форсирует пересчёт позиции, когда DOM ещё не успел отрисовать якорь
  const [tick, setTick] = useState(0);

  const step = STEPS[stepIndex];
  const total = STEPS.length;

  // F2.4 funnel analytics: emit started once on first mount, step_viewed on
  // each step (incl. step 0), and the terminal completed/skipped events from
  // their respective handlers below.
  useEffect(() => {
    if (visible) emit('tour:started', { total_steps: total });
    // mount-only intentionally; visibility doesn't change after first paint
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!visible || !step) return;
    emit('tour:step_viewed', { step: stepIndex, selector: step.selector });
  }, [visible, step, stepIndex]);

  // Пересчёт позиции карточки относительно якоря.
  // setState inside useLayoutEffect is intentional: we must read DOM layout (getBoundingClientRect)
  // and immediately update position state before the browser paints to avoid flicker.
  useLayoutEffect(() => {
    if (!visible || !step) return;
    const el = document.querySelector<HTMLElement>(`[data-tour="${step.selector}"]`);
    if (!el) {
      // Якорь ещё не смонтировался — повторим попытку на следующем тике
      const timer = window.setTimeout(() => setTick((v) => v + 1), 100);
      return () => window.clearTimeout(timer);
    }
    const rect = el.getBoundingClientRect();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- must sync position from DOM layout immediately; useLayoutEffect is the correct hook for this
    setPos(computePosition(rect));
    setAnchor({
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
      ready: true,
    });
  }, [visible, step, tick]);

  // Resize → пересчёт
  useEffect(() => {
    if (!visible) return;
    const onResize = () => setTick((v) => v + 1);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [visible]);

  // Esc = skip
  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        markCompleted('skipped');
        emit('tour:skipped', { step: stepIndex, total_steps: total });
        setVisible(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stepIndex/total captured at registration time; re-registering on each step would be needlessly noisy
  }, [visible]);

  // Outside-click = skip. Previously we rendered a full-screen
  // `<div class="fixed inset-0" onClick={handleSkip} />` to catch this, but
  // that div ALSO blocked mouseover events from reaching the map polygons
  // beneath it — the choropleth tooltips silently broke for any user with
  // the tour still active. Document-level mousedown achieves the same skip
  // without putting any pointer-events-grabbing element in the DOM.
  useEffect(() => {
    if (!visible) return;
    const onMouseDown = (e: MouseEvent) => {
      const card = document.querySelector('[data-tour-card]');
      const anchor = step
        ? document.querySelector(`[data-tour="${step.selector}"]`)
        : null;
      const target = e.target as Node | null;
      if (!target) return;
      // Click on the card itself or on the highlighted anchor → don't skip;
      // the user is either reading the tour or interacting with the very
      // element the tour is pointing at.
      if (card?.contains(target)) return;
      if (anchor?.contains(target)) return;
      markCompleted('skipped');
      emit('tour:skipped', { step: stepIndex, total_steps: total });
      setVisible(false);
    };
    // capture-phase: stop before bubbling reaches Leaflet's own handlers
    document.addEventListener('mousedown', onMouseDown, true);
    return () => document.removeEventListener('mousedown', onMouseDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- same reason as the Esc handler above
  }, [visible, step]);

  if (!visible || !step) return null;

  const isLast = stepIndex === total - 1;

  const handleNext = () => {
    if (isLast) {
      markCompleted('done');
      emit('tour:completed', { total_steps: total });
      setVisible(false);
    } else {
      setStepIndex((i) => i + 1);
    }
  };

  const handleSkip = () => {
    markCompleted('skipped');
    setVisible(false);
  };

  const padding = 6;

  return (
    <>
      {/* Backdrop with a hole around the anchor (via huge box-shadow). */}
      {anchor.ready ? (
        <div
          className="pointer-events-none fixed"
          aria-hidden="true"
          style={{
            zIndex: 1299,
            top: anchor.top - padding,
            left: anchor.left - padding,
            width: anchor.width + padding * 2,
            height: anchor.height + padding * 2,
            borderRadius: 12,
            // huge spread = covers viewport; rgba alpha keeps the cutout area fully readable
            boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.45)',
            outline: '2px solid rgba(59, 130, 246, 0.9)',
            outlineOffset: 0,
          }}
        />
      ) : (
        <div
          className="pointer-events-none fixed inset-0 bg-slate-900/30"
          style={{ zIndex: 1299 }}
          aria-hidden="true"
        />
      )}
      {/* Outside-click skip is handled by a document-level listener (see
          useEffect above) so the underlying map stays interactive — no
          full-screen overlay div that would steal hover/click events from
          the choropleth polygons. */}
      {/* Card */}
      <div
        data-tour-card
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-tour-title"
        className="fixed max-w-xs rounded-lg bg-white p-4 shadow-2xl"
        style={{
          zIndex: 1300,
          top: pos.top,
          left: pos.left,
          visibility: pos.ready ? 'visible' : 'hidden',
        }}
      >
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          {t('tour.step', { current: stepIndex + 1, total })}
        </div>
        <h3 id="onboarding-tour-title" className="mb-1 text-sm font-semibold text-slate-900">
          {t(step.titleKey, terms)}
        </h3>
        <p className="mb-3 text-xs leading-relaxed text-slate-600">{t(step.bodyKey, terms)}</p>
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={handleSkip}
            className="text-xs text-slate-500 hover:text-slate-700"
          >
            {t('tour.skip')}
          </button>
          <button
            type="button"
            onClick={handleNext}
            className="rounded bg-[color:var(--color-terra-600)] px-3 py-1.5 text-xs font-semibold text-[color:var(--color-paper)] hover:bg-[color:var(--color-terra-700)]"
          >
            {isLast ? t('tour.done') : t('tour.next')}
          </button>
        </div>
      </div>
    </>
  );
}
